import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Construction, MapPin, CheckCircle2, AlertTriangle, ArrowLeft, Wrench, PackageOpen, Loader2 } from "lucide-react";
import { fmtRoDate } from "../../lib/dateFmt";
import { errMsg } from "../../lib/apiErrors";

const STATUS_META = {
  on_site:                { label: "Pe șantier",   color: "bg-orange-100 text-orange-800 border-orange-300" },
  picked_up:              { label: "Luat",         color: "bg-blue-100 text-blue-800 border-blue-300" },
  returned_to_warehouse:  { label: "Descărcat",    color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  verified:               { label: "Verificat ✓", color: "bg-zinc-100 text-zinc-700 border-zinc-300" },
};

export default function SiteLeftoversPage() {
  const navigate = useNavigate();
  const { isAdmin, canDo } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [projectFilter, setProjectFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [actionDialog, setActionDialog] = useState(null); // { action, item }

  const canEdit = canDo("INVENTAR", "edit") || isAdmin;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/inventory/site-leftovers?status=${statusFilter}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(errMsg(e, "Eroare la încărcare"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const projects = useMemo(() => {
    const set = new Map();
    rows.forEach((r) => {
      const p = (r.on_site || {});
      if (p.project_id) set.set(p.project_id, p.project_label || p.project_id);
    });
    return Array.from(set.entries()).map(([id, label]) => ({ id, label }));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((it) => projectFilter === "all" || (it.on_site || {}).project_id === projectFilter)
      .filter((it) => !q || `${it.name || ""} ${it.serial || ""} ${(it.on_site || {}).project_label || ""}`.toLowerCase().includes(q));
  }, [rows, projectFilter, search]);

  // Group by project for tablou
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of filtered) {
      const key = (r.on_site || {}).project_id || "(fără proiect)";
      const label = (r.on_site || {}).project_label || "(fără proiect)";
      if (!m.has(key)) m.set(key, { label, items: [] });
      m.get(key).items.push(r);
    }
    return Array.from(m.entries()).sort(([, a], [, b]) => a.label.localeCompare(b.label, "ro"));
  }, [filtered]);

  const stats = useMemo(() => {
    const counters = { on_site: 0, picked_up: 0, returned_to_warehouse: 0, verified: 0 };
    rows.forEach((r) => {
      const s = (r.on_site || {}).status;
      if (counters[s] != null) counters[s] += 1;
    });
    return counters;
  }, [rows]);

  return (
    <div data-testid="page-site-leftovers">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 text-sm">
            <ArrowLeft size={14}/> Înapoi
          </button>
          <div>
            <h1 className="font-display text-2xl font-extrabold flex items-center gap-2">
              <Construction size={22} className="text-orange-600"/>
              Echipamente Șantier
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">Audit complet: ce a rămas pe șantier, ce s-a luat, unde s-a descărcat, ce trebuie verificat.</p>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-3" data-testid="kpi-on-site">
          <div className="text-[10px] uppercase tracking-wider text-orange-800 font-bold">Pe șantier</div>
          <div className="font-display text-2xl font-extrabold text-orange-900 tabular-nums">{stats.on_site}</div>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3" data-testid="kpi-picked-up">
          <div className="text-[10px] uppercase tracking-wider text-blue-800 font-bold">Luate</div>
          <div className="font-display text-2xl font-extrabold text-blue-900 tabular-nums">{stats.picked_up}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="kpi-returned">
          <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Descărcate</div>
          <div className="font-display text-2xl font-extrabold text-emerald-900 tabular-nums">{stats.returned_to_warehouse}</div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="kpi-verified">
          <div className="text-[10px] uppercase tracking-wider text-zinc-700 font-bold">Verificate</div>
          <div className="font-display text-2xl font-extrabold text-zinc-800 tabular-nums">{stats.verified}</div>
        </div>
      </div>

      <div className="card-flat p-3 mb-3 flex items-center gap-2 flex-wrap">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 caută..." className="h-9 max-w-[260px]" data-testid="sl-search"/>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[180px]" data-testid="sl-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active (≠ verificate)</SelectItem>
            <SelectItem value="all">Toate</SelectItem>
            <SelectItem value="on_site">Doar pe șantier</SelectItem>
            <SelectItem value="picked_up">Doar luate</SelectItem>
            <SelectItem value="returned_to_warehouse">Doar descărcate</SelectItem>
            <SelectItem value="verified">Doar verificate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="h-9 w-[220px]"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate proiectele</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-zinc-500">{filtered.length} {filtered.length === 1 ? "item" : "items"}</div>
      </div>

      {loading ? (
        <div className="card-flat p-12 text-center text-zinc-500"><Loader2 className="inline animate-spin mr-2" size={16}/>Se încarcă...</div>
      ) : groups.length === 0 ? (
        <div className="card-flat p-12 text-center text-zinc-500 italic" data-testid="sl-empty">
          Niciun echipament în acest filtru.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(([pid, g]) => (
            <div key={pid} className="card-flat overflow-hidden" data-testid={`sl-group-${pid}`}>
              <div className="px-4 py-2 bg-orange-50 border-b border-orange-200 font-display font-bold text-sm uppercase tracking-wider text-orange-900 flex items-center justify-between">
                <div>🚧 {g.label}</div>
                <div className="text-[11px] text-orange-700 normal-case font-normal">{g.items.length} items</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="p-2">Status</th>
                      <th className="p-2">Echipament</th>
                      <th className="p-2">Lăsat de</th>
                      <th className="p-2">Loc original</th>
                      <th className="p-2">Acțiuni</th>
                      <th className="p-2 text-right">Detalii</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it) => {
                      const os = it.on_site || {};
                      const meta = STATUS_META[os.status] || { label: os.status, color: "bg-zinc-100 text-zinc-700 border-zinc-200" };
                      return (
                        <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`sl-row-${it.id}`}>
                          <td className="p-2"><span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`}>{meta.label}</span></td>
                          <td className="p-2">
                            <div className="font-medium">{it.name}</div>
                            <div className="text-[10px] text-zinc-500 flex gap-2 flex-wrap mt-0.5">
                              {it.color && <span>{it.color}</span>}
                              {it.serial && <span className="font-mono">SN: {it.serial}</span>}
                            </div>
                          </td>
                          <td className="p-2 text-[11px] text-zinc-600">
                            <div>{os.left_by || "—"}</div>
                            <div className="text-[10px] text-zinc-400">{os.left_at ? fmtRoDate(os.left_at.slice(0, 10)) : ""}</div>
                          </td>
                          <td className="p-2 text-[11px] text-zinc-600">
                            <div>📍 {os.original_location || "—"}</div>
                            {os.original_shelf_row && os.original_shelf_col && <div className="text-[10px]">Raft {os.original_shelf_row}{os.original_shelf_col}</div>}
                          </td>
                          <td className="p-2">
                            <div className="flex gap-1 flex-wrap">
                              {canEdit && os.status === "on_site" && (
                                <Button size="sm" variant="outline" onClick={() => setActionDialog({ action: "pickup", item: it })} className="h-7 text-[11px] border-blue-400 text-blue-700 hover:bg-blue-50" data-testid={`sl-action-pickup-${it.id}`}>📦 Luat</Button>
                              )}
                              {canEdit && (os.status === "on_site" || os.status === "picked_up") && (
                                <Button size="sm" variant="outline" onClick={() => setActionDialog({ action: "return", item: it })} className="h-7 text-[11px] border-emerald-400 text-emerald-700 hover:bg-emerald-50" data-testid={`sl-action-return-${it.id}`}>🏠 Descarcă</Button>
                              )}
                              {isAdmin && os.status === "returned_to_warehouse" && (
                                <Button size="sm" variant="outline" onClick={() => setActionDialog({ action: "verify", item: it })} className="h-7 text-[11px] border-zinc-400 text-zinc-700 hover:bg-zinc-100" data-testid={`sl-action-verify-${it.id}`}>✓ Verifică</Button>
                              )}
                            </div>
                          </td>
                          <td className="p-2 text-right text-[10px] text-zinc-500">
                            {os.picked_up_by && <div>📦 {os.picked_up_by}</div>}
                            {os.return_location && <div>🏠 {os.return_location}</div>}
                            {os.defect && <div className="text-red-700 font-bold">⚠ DEFECT</div>}
                            {os.verified_by && <div className="text-emerald-700 font-bold">✓ {os.verified_by}</div>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {actionDialog && (
        <ActionDialog
          action={actionDialog.action}
          item={actionDialog.item}
          onClose={() => setActionDialog(null)}
          onDone={() => { setActionDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function ActionDialog({ action, item, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [returnLocation, setReturnLocation] = useState(item.on_site?.original_location || item.location || "MAGAZIE");
  const [defect, setDefect] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      let url = `/inventory/site-leftovers/${item.id}/${action}`;
      let body = {};
      if (action === "pickup") body = { note: note.trim() || null };
      if (action === "return") body = { return_location: returnLocation.trim() || null, note: note.trim() || null, defect };
      // verify nu primește body
      await api.post(url, body);
      const messages = {
        pickup: "📦 Luat de pe șantier",
        return: `🏠 Descărcat la „${returnLocation}"${defect ? " (DEFECT)" : ""}`,
        verify: "✓ Verificat",
      };
      toast.success(messages[action]);
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e, "Eroare"));
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    pickup: "Confirmă luarea de pe șantier",
    return: "Descarcă în magazie",
    verify: "Verifică descărcarea",
  };
  const Icons = { pickup: PackageOpen, return: MapPin, verify: CheckCircle2 };
  const Icon = Icons[action];

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose && onClose(); }}>
      <DialogContent className="max-w-md" data-testid={`sl-action-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon size={18}/>{titles[action]}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            <strong>{item.name}</strong>
            {item.serial && <span className="font-mono ml-2">SN: {item.serial}</span>}
            <br/>De pe șantier: <strong>{item.on_site?.project_label || "—"}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {action === "return" && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
                💡 <strong>Locația originală</strong> (unde să descarci): <strong>{item.on_site?.original_location || "—"}</strong>
                {item.on_site?.original_shelf_row && item.on_site?.original_shelf_col && (
                  <> · Raft <strong>{item.on_site.original_shelf_row}{item.on_site.original_shelf_col}</strong></>
                )}
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Locație descărcare</Label>
                <Input value={returnLocation} onChange={(e) => setReturnLocation(e.target.value)} className="h-9 text-sm" data-testid="sl-return-location"/>
              </div>
              <label className="flex items-center gap-2 text-sm text-red-700 font-semibold cursor-pointer">
                <input type="checkbox" checked={defect} onChange={(e) => setDefect(e.target.checked)} data-testid="sl-return-defect"/>
                ⚠ Echipamentul este DEFECT (trimite la reparații)
              </label>
            </>
          )}
          {action !== "verify" && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Notă (opțional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="text-sm" data-testid="sl-action-note"/>
            </div>
          )}
          {action === "verify" && (
            <div className="text-sm text-zinc-700 py-2">
              Confirmi că <strong>{item.name}</strong> a fost descărcat corect în <strong>{item.on_site?.return_location || "—"}</strong>?
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sl-action-submit">
            {busy ? <><Loader2 className="animate-spin mr-1" size={14}/>...</> : "Confirmă"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
